import React, { useState } from 'react';
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonText, IonSpinner, IonToolbar, IonButtons, IonIcon, IonRow, IonCol, IonGrid, IonInput } from '@ionic/react';
import { arrowBack, mail } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import { getDeviceInfo } from '@rsmaids/shared/utils/deviceUtils';
import { useTranslation } from 'react-i18next';
import { markPasskeyUsedOnDevice } from '../../utils/passkeyUtils';

// Cache device info at module level for performance
let cachedDeviceInfo: Awaited<ReturnType<typeof getDeviceInfo>> | null = null;

const getOrCacheDeviceInfo = async () => {
  if (!cachedDeviceInfo) {
    cachedDeviceInfo = await getDeviceInfo();
  }
  return cachedDeviceInfo;
};

interface PasskeyAuthProps {
  email?: string;
  isAuthenticated?: boolean; // New prop to determine which flow to use
  onSuccess?: (userData: { user: { uid: string; email: string; customToken: string } | null; token: string; authenticated?: boolean; registered?: boolean }) => void;
  onError?: (error: Error) => void;
  onGoBack?: () => void;
}

export const PasskeyAuth: React.FC<PasskeyAuthProps> = ({ email, isAuthenticated = false, onSuccess, onError, onGoBack }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  // Send verification code for unauthenticated users
  const handleSendCode = async () => {
    if (!email) {
      if (onError) onError(new Error('Email is required'));
      return;
    }

    setSendingCode(true);
    try {
      console.log('[PasskeyAuth] Requesting passkey verification code for:', email);
      const result = await rsAuth.sendPasskeyVerificationCode(email);
      console.log('[PasskeyAuth] Passkey verification code result:', result);
      
      if (result.success) {
        setCodeSent(true);
      } else {
        // Handle ApiError object or string error
        let errorMessage = t('components.AuthComponents.PasskeyAuth.failedToSendVerificationCode');
        if (result.error) {
          if (typeof result.error === 'string') {
            errorMessage = result.error;
          } else if (typeof result.error === 'object' && 'message' in result.error) {
            errorMessage = result.error.message;
          }
        } else if (result.message) {
          errorMessage = result.message;
        }
        console.error('[PasskeyAuth] Verification code error:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('[PasskeyAuth] Failed to send verification code:', error);
      if (onError) onError(error instanceof Error ? error : new Error('Failed to send verification code'));
    } finally {
      setSendingCode(false);
    }
  };

  const handlePasskeyRegister = async () => {
    if (!email) {
      if (onError) onError(new Error('Email is required'));
      return;
    }

    // For unauthenticated users, require verification code
    if (!isAuthenticated && !verificationCode) {
      if (onError) onError(new Error('Verification code is required'));
      return;
    }

    setIsLoading(true);
    setLoadingText(t('components.AuthComponents.PasskeyAuth.startingPasskeyRegistration'));

    try {
      // Get device info (cached for performance)
      const deviceInfo = await getOrCacheDeviceInfo();

      setLoadingText(t('components.AuthComponents.PasskeyAuth.pleaseCreateYourPasskey'));

      // Use the appropriate method based on authentication status
      const result = isAuthenticated 
        ? await rsAuth.registerWithPasskey(email, { ...deviceInfo })
        : await rsAuth.registerWithPasskeyAndCode(email, verificationCode, { ...deviceInfo });

      if (!result.success) {
        throw new Error(result.message || t('components.AuthComponents.PasskeyAuth.failedToRegisterPasskey'));
      }

      setLoadingText(t('components.AuthComponents.PasskeyAuth.completingRegistration'));

      // If registration returns user data with custom token, sign in
      if (result.user?.customToken) {
        console.log('PasskeyAuth: Storing session data from passkey registration');

        // User is already mapped to UserInfo format by rsAuthClient
        const userInfo = result.user;

        // Create session data from the user info
        const sessionData = {
          user: userInfo,
          firebaseToken: result.user.customToken,
          loginTimestamp: Date.now()
        };

        localStorage.setItem('session', JSON.stringify(sessionData));
        console.log('PasskeyAuth: Session data stored with role:', userInfo.role);
        
        // Mark that user has successfully registered/used a passkey
        markPasskeyUsedOnDevice(email || '');

        // Sign in with Firebase using the custom token
        console.log('PasskeyAuth: Attempting Firebase sign-in with custom token');
        try {
          const { signInWithCustomToken } = await import('firebase/auth');
          const { auth } = await import('../../auth/firebase');
          await signInWithCustomToken(auth, result.user.customToken);
          console.log('PasskeyAuth: Firebase sign-in successful');
        } catch (tokenError) {
          console.error('PasskeyAuth: Failed to sign in with custom token:', tokenError);
          throw new Error('Registration completed but sign-in failed');
        }

        console.log('PasskeyAuth: Calling onSuccess callback with authentication');
        if (onSuccess) {
          onSuccess({
            user: {
              uid: result.user.uid || '',
              email: result.user.email || '',
              customToken: result.user.customToken
            },
            token: result.user.customToken,
            authenticated: true,
            registered: true
          });
        }
      } else {
        // Registration completed but no user data (shouldn't happen with new flow)
        console.log('PasskeyAuth: Registration completed without authentication');
        if (onSuccess) onSuccess({ user: null, token: '', registered: true });
      }
    } catch (error) {
      console.error('Passkey registration failed:', error);
      if (onError) onError(error instanceof Error ? error : new Error('Registration failed'));
    } finally {
      setIsLoading(false);
      setLoadingText('');
    }
  };

  return (
    <>
      <IonCard className={`${styles.loginCard} ion-text-center`}>
        <IonCardHeader className={styles.cardHeader}>
          <IonToolbar>
            <IonButtons slot="start">
              {onGoBack && (
                <IonButton
                  fill="clear"
                  shape='round'
                  className={styles.backButton}
                  onClick={onGoBack}
                  disabled={isLoading || sendingCode}
                >
                  <IonIcon icon={arrowBack} />
                </IonButton>
              )}
            </IonButtons>
            <IonCardTitle className={styles.cardTitle}>{t('components.AuthComponents.PasskeyAuth.createYourPasskey')}</IonCardTitle>
          </IonToolbar>
        </IonCardHeader>
        <IonCardContent>
          <IonText>
            <p>{t('components.AuthComponents.PasskeyAuth.setUpPasswordlessLoginUsingYour')}</p>
          </IonText>
          <IonGrid>
            {/* Show verification code input for unauthenticated users */}
            {!isAuthenticated && !codeSent && (
              <IonRow>
                <IonCol className={styles.centerCol}>
                  <IonText color="medium">
                    <p>{t('components.AuthComponents.PasskeyAuth.weLlSendAVerificationCode')} <strong>{email}</strong>{t('components.AuthComponents.PasskeyAuth.toConfirmYourIdentity')}</p>
                  </IonText>
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={handleSendCode}
                    className={`${styles.customButton} ${styles.mainButton} ${sendingCode ? styles.loadingButton : ''}`}
                    disabled={sendingCode}
                  >
                    {sendingCode ? (
                      <>
                        <IonSpinner name="bubbles" slot="start" />
                        <span>{t('components.AuthComponents.PasskeyAuth.sendingCode')}</span>
                      </>
                    ) : (
                      <>
                        <IonIcon icon={mail} slot="start" />{t('components.AuthComponents.PasskeyAuth.sendVerificationCode')}
                      </>
                    )}
                  </IonButton>
                </IonCol>
              </IonRow>
            )}

            {/* Show verification code input after code is sent */}
            {!isAuthenticated && codeSent && (
              <IonRow>
                <IonCol>
                  <IonInput
                    className={`${styles.customInput} ion-margin-top`}
                    labelPlacement="stacked"
                    label={t('components.AuthComponents.PasskeyAuth.verificationCode')}
                    type="text"
                    placeholder={t('components.AuthComponents.PasskeyAuth.enter6DigitCode')}
                    value={verificationCode}
                    onIonInput={(e) => setVerificationCode(e.detail.value || '')}
                    disabled={isLoading}
                    maxlength={6}
                    required
                  >
                    <IonIcon icon={mail} slot="start" />
                  </IonInput>
                  <IonText color="medium" className="ion-margin-top">
                    <p><small>{t('components.AuthComponents.PasskeyAuth.checkYourEmailForTheVerification')}</small></p>
                  </IonText>
                  {/* Resend code link */}
                  <IonText 
                    color="primary" 
                    className="ion-margin-top ion-text-center"
                    onClick={handleSendCode}
                  >
                    <p className="ion-no-margin">
                      <small className={styles.clickableText}>
                        {sendingCode ? t('components.AuthComponents.PasskeyAuth.sending') : t('components.AuthComponents.PasskeyAuth.didnTReceiveTheCodeResend')}
                      </small>
                    </p>
                  </IonText>
                </IonCol>
              </IonRow>
            )}

            {/* Show create passkey button for authenticated users OR after code is entered */}
            {(isAuthenticated || codeSent) && (
              <IonRow>
                <IonCol className={styles.centerCol}>
                  <IonButton
                    expand="block"
                    fill="solid"
                    onClick={handlePasskeyRegister}
                    className={`${styles.customButton} ${styles.mainButton} ${isLoading ? styles.loadingButton : ''}`}
                    disabled={isLoading || (!isAuthenticated && !verificationCode)}
                  >
                    {isLoading ? (
                      <>
                        <IonSpinner name="bubbles" slot="start" />
                        <span>{loadingText}</span>
                      </>
                    ) : (
                      t('components.AuthComponents.PasskeyAuth.createNewPasskey')
                    )}
                  </IonButton>
                </IonCol>
              </IonRow>
            )}

            <IonText color="medium" className="ion-margin-top">
              <p><small>{t('components.AuthComponents.PasskeyAuth.passkeysAreMoreSecureThanPasswords')}</small></p>
            </IonText>
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </>
  );
};